import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  COMMUNICATIONS_ROLE_OPTIONS,
  DEFAULT_REGISTRATION_COUNTRY,
  ER_STAKEHOLDER_TYPES,
  JOB_TITLE_OPTIONS,
  requiresCommunicationsRoleJobTitle,
  STAKEHOLDER_OPTIONS,
  STAKEHOLDER_EMERGENCY_RESPONDER,
  buildExpressAccountSetupCrmPayload,
  expressRegistrationSuccessParagraphs,
  formatUsPhoneTyping,
  generateExpressTemporaryPassword,
  listRegistrationCountiesForState,
  listRegistrationStatesForCountry,
  parseAccountSetupCrmResponse,
  parseRegistrationUrlParams,
  registrationExpressSchema,
  registrationOutcomeFromResolvedStatus,
  resolveRegistrationStakeholderType,
  toRegisterBuxusRequest,
  type RegistrationExpressValues,
} from "@buxus/shared";
import { useAbp } from "../platform/abp/react";
import {
  accountSetupCrmTracked,
  findUserByEmailInBuxusTracked,
  flattenZodFieldErrors,
  registerBuxusAccountTracked,
  registrationFlowContextFromUrl,
  trackRegistrationButtonPressed,
  trackRegistrationFormValidationFailed,
  trackRegistrationPageViewed,
  type RegistrationStepAnalyticsContext,
} from "../platform/analytics/registrationAnalytics";
import { RegPasswordField } from "../ui/RegPasswordField";
import { RegReqLabel } from "../ui/RegReqLabel";
import { friendlyRegistrationError } from "./registrationErrors";
import { reportRegistrationFailureIfServer } from "../platform/errors/webFailureReporting";
import { leaveRegistrationHost } from "./registrationHostBridge";
import {
  detectRegistrationHostKind,
  RegistrationShell,
  useRegistrationScrollToTop,
  type RegistrationHostKind,
} from "./registrationShell";
import "./RegistrationFlow.css";

const EXPRESS_DEFAULTS: RegistrationExpressValues = {
  stakeholderType: STAKEHOLDER_EMERGENCY_RESPONDER,
  jobTitle: "",
  isUnaffiliatedProfessionalErTrainer: "",
  communicationsRole: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  state: "",
  county: "",
  password: "",
  passwordConfirm: "",
};

export function ExpressRegistrationFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = useMemo(() => parseRegistrationUrlParams(location.search), [location.search]);
  const { isAuthenticated, logout } = useAbp();
  const [hostKind] = useState<RegistrationHostKind>(detectRegistrationHostKind);

  const [form, setForm] = useState<RegistrationExpressValues>({ ...EXPRESS_DEFAULTS });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [resolvedStatus, setResolvedStatus] = useState<string | null>(null);
  const scrollRegistrationToTop = useRegistrationScrollToTop();

  const regFlowCtx = useMemo(
    () => registrationFlowContextFromUrl(urlParams, hostKind === "webview"),
    [urlParams, hostKind],
  );

  const regStepCtx = useMemo((): RegistrationStepAnalyticsContext => {
    return {
      ...regFlowCtx,
      totalSteps: 1,
      stakeholderType: resolveRegistrationStakeholderType(
        form.stakeholderType,
        form.jobTitle,
        form.isUnaffiliatedProfessionalErTrainer,
      ),
      emailAddress: form.email.trim() || undefined,
      hasQuickCode: false,
      hasReferrerContactId: false,
    };
  }, [regFlowCtx, form.stakeholderType, form.jobTitle, form.isUnaffiliatedProfessionalErTrainer, form.email]);

  useEffect(() => {
    trackRegistrationPageViewed("Express Registration", regFlowCtx);
  }, [regFlowCtx]);

  useEffect(() => {
    if (isComplete) scrollRegistrationToTop();
  }, [isComplete, scrollRegistrationToTop]);

  const promptPassword = urlParams.promptPassword;
  const eventTitle = urlParams.eventTitle;
  const schema = useMemo(() => registrationExpressSchema(promptPassword), [promptPassword]);

  const stateOptions = useMemo(
    () => listRegistrationStatesForCountry(DEFAULT_REGISTRATION_COUNTRY),
    [],
  );

  const countyOptions = useMemo(
    () => listRegistrationCountiesForState(DEFAULT_REGISTRATION_COUNTRY, form.state),
    [form.state],
  );

  const showJobTitle = ER_STAKEHOLDER_TYPES.includes(
    form.stakeholderType as (typeof ER_STAKEHOLDER_TYPES)[number],
  );

  const fieldErrors = useMemo(() => {
    const r = schema.safeParse(form);
    if (r.success) return {};
    const fe = r.error.flatten().fieldErrors;
    return {
      stakeholderType: fe.stakeholderType?.[0],
      jobTitle: fe.jobTitle?.[0],
      isUnaffiliatedProfessionalErTrainer: fe.isUnaffiliatedProfessionalErTrainer?.[0],
      communicationsRole: fe.communicationsRole?.[0],
      firstName: fe.firstName?.[0],
      lastName: fe.lastName?.[0],
      phone: fe.phone?.[0],
      email: fe.email?.[0],
      state: fe.state?.[0],
      county: fe.county?.[0],
      password: fe.password?.[0],
      passwordConfirm: fe.passwordConfirm?.[0],
    };
  }, [schema, form]);

  function fieldError(key: keyof typeof fieldErrors): string | undefined {
    const msg = fieldErrors[key];
    if (!msg) return undefined;
    if (submitAttempted) return msg;
    return undefined;
  }

  const successParagraphs = useMemo(() => {
    const outcome = registrationOutcomeFromResolvedStatus(resolvedStatus);
    return expressRegistrationSuccessParagraphs(promptPassword, outcome);
  }, [promptPassword, resolvedStatus]);

  const setField = <K extends keyof RegistrationExpressValues>(
    key: K,
    value: RegistrationExpressValues[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetForNextRegistrant = () => {
    setForm({ ...EXPRESS_DEFAULTS });
    setSubmitAttempted(false);
    setIsComplete(false);
    setSubmitError(null);
    setResolvedStatus(null);
  };

  const handleStartOver = () => {
    trackRegistrationButtonPressed("Start Over", regFlowCtx, {
      screen: "Express Registration Success",
      resolvedStatus: resolvedStatus ?? undefined,
    });
    resetForNextRegistrant();
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitAttempted(true);
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const { errorFields, errorMessages } = flattenZodFieldErrors(parsed.error);
      trackRegistrationFormValidationFailed({
        formName: "ExpressRegistrationForm",
        errorFields,
        errorMessages,
        ...regStepCtx,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const email = parsed.data.email.trim();
      const existing = await findUserByEmailInBuxusTracked(email, regFlowCtx);
      if (existing?.zohoCrmId?.trim() || existing?.userId?.trim()) {
        setSubmitError(
          "This email is already associated with an existing account. Please sign in, reset your password, or use the standard registration path.",
        );
        return;
      }

      const passwordToUse = promptPassword
        ? (parsed.data.password ?? "").trim()
        : generateExpressTemporaryPassword();

      const registerOut = await registerBuxusAccountTracked(
        toRegisterBuxusRequest({
          stakeholderType: parsed.data.stakeholderType,
          jobTitle: parsed.data.jobTitle,
          isUnaffiliatedProfessionalErTrainer: parsed.data.isUnaffiliatedProfessionalErTrainer,
          communicationsRole: parsed.data.communicationsRole,
          firstName: parsed.data.firstName,
          lastName: parsed.data.lastName,
          phone: parsed.data.phone,
          email: parsed.data.email,
          password: passwordToUse,
          passwordConfirm: passwordToUse,
        }),
        regStepCtx,
      );

      const abpUserId = registerOut?.userId?.trim();
      if (!abpUserId) {
        setSubmitError("Registration did not return a user id. Please try again.");
        return;
      }

      const body = buildExpressAccountSetupCrmPayload({
        abpUserId,
        values: parsed.data,
        approvalCode: urlParams.approvalCode || null,
        eventCodeSlug: urlParams.eventCode || null,
      });

      const res = await accountSetupCrmTracked(body, {
        userId: abpUserId,
        emailAddress: email,
        stakeholderType: parsed.data.stakeholderType,
        hasQuickCode: false,
        hasReferrerContactId: false,
        ...regFlowCtx,
      });
      const { ok, message, resolvedStatus: crmStatus } = parseAccountSetupCrmResponse(res);
      if (!ok) {
        setSubmitError(message ?? "Your registration could not be completed. Please try again.");
        return;
      }

      setResolvedStatus(crmStatus ?? null);
      setIsComplete(true);
    } catch (error) {
      reportRegistrationFailureIfServer(error, "expressRegistration");
      setSubmitError(friendlyRegistrationError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Signed-in is local (token + userId in AbpProvider). No wait on remote probeSession.
  if (isAuthenticated) {
    return (
      <RegistrationShell hostKind={hostKind}>
        <section className="reg-card">
          <h2 className="reg-step-title">Already Signed In</h2>
          <p className="reg-step-lead">
            You are signed in to Buxus. To register a new account, please sign out first.
          </p>
          <div className="reg-actions">
            <button type="button" className="reg-btn primary" onClick={() => void logout()}>
              Log Out
            </button>
            <button type="button" className="reg-btn subtle" onClick={() => leaveRegistrationHost(navigate)}>
              Home
            </button>
          </div>
        </section>
      </RegistrationShell>
    );
  }

  if (isComplete) {
    return (
      <RegistrationShell hostKind={hostKind}>
        <section className="reg-card">
          <h2 className="reg-step-title">{eventTitle}</h2>
          <p className="reg-step-lead reg-step-lead--tight">
            <strong>Your Buxus account has been created.</strong>
          </p>
          {successParagraphs.map((paragraph) => (
            <p key={paragraph} className="reg-step-lead">
              {paragraph}
            </p>
          ))}
          <div className="reg-actions">
            <button type="button" className="reg-btn primary" onClick={handleStartOver}>
              Start Over
            </button>
          </div>
        </section>
      </RegistrationShell>
    );
  }

  return (
    <RegistrationShell hostKind={hostKind}>
      <section className="reg-card">
          <h2 className="reg-step-title">{eventTitle}</h2>
          <p className="reg-step-lead">
            Start your Buxus app account now with quick registration. You can complete your full profile
            details later.
          </p>

          {submitError ? <p className="reg-error">{submitError}</p> : null}

          <div className="reg-grid">
            <label className="reg-field">
              <RegReqLabel required>Are you a?</RegReqLabel>
              <select
                value={form.stakeholderType}
                onChange={(e) => {
                  const next = e.target.value;
                  const isEr = ER_STAKEHOLDER_TYPES.includes(next as (typeof ER_STAKEHOLDER_TYPES)[number]);
                  setForm((prev) => ({
                    ...prev,
                    stakeholderType: next,
                    jobTitle: isEr ? prev.jobTitle : "",
                    isUnaffiliatedProfessionalErTrainer: isEr
                      ? prev.isUnaffiliatedProfessionalErTrainer
                      : "",
                    communicationsRole: isEr ? prev.communicationsRole : "",
                  }));
                }}
              >
                {STAKEHOLDER_OPTIONS.map((x) => (
                  <option key={x.value} value={x.value}>
                    {x.label}
                  </option>
                ))}
              </select>
              {fieldError("stakeholderType") ? (
                <span className="reg-field-error">{fieldError("stakeholderType")}</span>
              ) : null}
            </label>

            {showJobTitle ? (
              <label className="reg-field">
                <RegReqLabel required>Job Title</RegReqLabel>
                <select
                  value={form.jobTitle}
                  onChange={(e) => {
                    const next = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      jobTitle: next,
                      communicationsRole: requiresCommunicationsRoleJobTitle(next)
                        ? prev.communicationsRole
                        : "",
                    }));
                  }}
                >
                  <option value="">Select</option>
                  {JOB_TITLE_OPTIONS.map((title) => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  ))}
                </select>
                {fieldError("jobTitle") ? (
                  <span className="reg-field-error">{fieldError("jobTitle")}</span>
                ) : null}
              </label>
            ) : null}

            {requiresCommunicationsRoleJobTitle(form.jobTitle) ? (
              <label className="reg-field">
                <RegReqLabel required>Communications Role</RegReqLabel>
                <select
                  value={form.communicationsRole}
                  onChange={(e) => setField("communicationsRole", e.target.value)}
                >
                  <option value="">Select</option>
                  {COMMUNICATIONS_ROLE_OPTIONS.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
                {fieldError("communicationsRole") ? (
                  <span className="reg-field-error">{fieldError("communicationsRole")}</span>
                ) : null}
              </label>
            ) : null}

            {showJobTitle ? (
              <label className="reg-field reg-field--full">
                <RegReqLabel required>
                  Are you a professional ER trainer and not affiliated with any specific response
                  organization (other than a trade association)?
                </RegReqLabel>
                <select
                  value={form.isUnaffiliatedProfessionalErTrainer}
                  onChange={(e) => setField("isUnaffiliatedProfessionalErTrainer", e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
                {fieldError("isUnaffiliatedProfessionalErTrainer") ? (
                  <span className="reg-field-error">
                    {fieldError("isUnaffiliatedProfessionalErTrainer")}
                  </span>
                ) : null}
              </label>
            ) : null}

            <label className="reg-field">
              <RegReqLabel required>First Name</RegReqLabel>
              <input value={form.firstName} onChange={(e) => setField("firstName", e.target.value)} />
              {fieldError("firstName") ? (
                <span className="reg-field-error">{fieldError("firstName")}</span>
              ) : null}
            </label>

            <label className="reg-field">
              <RegReqLabel required>Last Name</RegReqLabel>
              <input value={form.lastName} onChange={(e) => setField("lastName", e.target.value)} />
              {fieldError("lastName") ? (
                <span className="reg-field-error">{fieldError("lastName")}</span>
              ) : null}
            </label>

            <label className="reg-field">
              <RegReqLabel required>Phone</RegReqLabel>
              <input
                value={form.phone}
                onChange={(e) => setField("phone", formatUsPhoneTyping(e.target.value))}
                inputMode="tel"
              />
              {fieldError("phone") ? <span className="reg-field-error">{fieldError("phone")}</span> : null}
            </label>

            <label className="reg-field">
              <RegReqLabel required>Email</RegReqLabel>
              <input
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                autoComplete="email"
                spellCheck={false}
              />
              {fieldError("email") ? <span className="reg-field-error">{fieldError("email")}</span> : null}
            </label>

            <label className="reg-field">
              <RegReqLabel required>State</RegReqLabel>
              <select
                value={form.state}
                onChange={(e) => {
                  setField("state", e.target.value);
                  setField("county", "");
                }}
              >
                <option value="">Select</option>
                {stateOptions.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
              {fieldError("state") ? <span className="reg-field-error">{fieldError("state")}</span> : null}
            </label>

            <label className="reg-field">
              <RegReqLabel required>County</RegReqLabel>
              <select
                value={form.county}
                disabled={!form.state}
                onChange={(e) => setField("county", e.target.value)}
              >
                <option value="">Select</option>
                {countyOptions.map((county) => (
                  <option key={county} value={county}>
                    {county}
                  </option>
                ))}
              </select>
              {fieldError("county") ? <span className="reg-field-error">{fieldError("county")}</span> : null}
            </label>

            {promptPassword ? (
              <>
                <RegPasswordField
                  label="Password"
                  required
                  value={form.password}
                  onChange={(v) => setField("password", v)}
                  autoComplete="new-password"
                  error={fieldError("password")}
                />
                <RegPasswordField
                  label="Password Confirm"
                  required
                  value={form.passwordConfirm}
                  matchesValue={form.password}
                  onChange={(v) => setField("passwordConfirm", v)}
                  autoComplete="new-password"
                  error={fieldError("passwordConfirm")}
                />
              </>
            ) : null}
          </div>

          <div className="reg-actions">
            <button
              type="button"
              className="reg-btn primary"
              disabled={isSubmitting}
              onClick={() => void handleSubmit()}
            >
              {isSubmitting ? "Registering…" : "Register for Buxus App"}
            </button>
          </div>
      </section>
    </RegistrationShell>
  );
}
